import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import InputComponent from "components/shared/InputComponent";
import TextArea from "components/shared/TextArea";
import Select from "components/shared/Select";
import { useTranslation } from "react-i18next";
import useAutoSave from "hooks/useAutoSave";
import AutoSaveIndicator from "components/shared/AutoSaveIndicator";
import { editPackage } from "services/Admin/Package.service";

// Convert the string-based form fields into the API shape, for only the
// changed fields (numbers parsed, features split to an array).
const NUMERIC_PACKAGE_FIELDS = [
    "price",
    "duration",
    "maxClasses",
    "maxStudents",
    "maxTeachers",
    "perTeacherCredits",
    "perStudentCredits",
];
const buildPackagePatch = (changed) => {
    const out = {};
    Object.keys(changed).forEach((k) => {
        if (k === "features") {
            out.features = String(changed.features || "")
                .split(",")
                .map((f) => f.trim())
                .filter(Boolean);
        } else if (NUMERIC_PACKAGE_FIELDS.includes(k)) {
            const n = k === "price" ? parseFloat(changed[k]) : parseInt(changed[k], 10);
            out[k] = Number.isNaN(n) ? 0 : n;
        } else {
            out[k] = changed[k];
        }
    });
    return out;
};

const CreatePackageModal = ({ show, handleClose, onSave, editData = null }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        price: "",
        duration: "",
        features: "",
        maxClasses: "",
        maxStudents: "",
        maxTeachers: "",
        perTeacherCredits: "",
        perStudentCredits: "",
        status: "active",
    });

    const [errors, setErrors] = useState({});

    // Auto-save edits to an EXISTING package directly to the DB (only changed
    // fields; never triggers anything but a package update). Creating a new
    // package still uses the explicit Create button (no record exists yet).
    const {
        status: pkgAutoStatus,
        lastSavedAt: pkgAutoAt,
        isOnline: pkgAutoOnline,
        markSaved: markPkgSaved,
        saveImmediately: savePkgImmediately,
    } = useAutoSave(
        formData,
        (changed) => editPackage(editData._id, buildPackagePatch(changed)),
        { enabled: Boolean(editData && editData._id) }
    );

    useEffect(() => {
        if (editData) {
            const nextForm = {
                name: editData.name || "",
                description: editData.description || "",
                price: editData.price || "",
                duration: editData.duration || "",
                features: Array.isArray(editData.features) ? editData.features.join(", ") : "",
                maxClasses: editData.maxClasses || "",
                maxStudents: editData.maxStudents || "",
                maxTeachers: editData.maxTeachers || "",
                perTeacherCredits: editData.perTeacherCredits || "",
                perStudentCredits: editData.perStudentCredits || "",
                status: editData.status || "active",
            };
            setFormData(nextForm);
            // Seed the baseline so the freshly-loaded package isn't re-saved.
            markPkgSaved(nextForm);
        } else {
            // Reset form when creating new package
            setFormData({
                name: "",
                description: "",
                price: "",
                duration: "",
                features: "",
                maxClasses: "",
                maxStudents: "",
                maxTeachers: "",
                perTeacherCredits: "",
                perStudentCredits: "",
                status: "active",
            });
        }
        setErrors({});
    }, [editData, show]);

    const handleChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Package name is required";
        }

        if (!formData.price || parseFloat(formData.price) < 0) {
            newErrors.price = "Valid price is required";
        }

        if (!formData.duration || parseInt(formData.duration) < 1) {
            newErrors.duration = "Valid duration is required";
        }

        if (!formData.maxStudents || parseInt(formData.maxStudents) < 1) {
            newErrors.maxStudents = "Max students is required";
        }

        if (!formData.maxTeachers || parseInt(formData.maxTeachers) < 1) {
            newErrors.maxTeachers = "Max teachers is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Parse features from comma-separated string to array
        const featuresArray = formData.features
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f);

        const packageData = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            price: parseFloat(formData.price),
            duration: parseInt(formData.duration),
            features: featuresArray,
            maxClasses: parseInt(formData.maxClasses) || 0,
            maxStudents: parseInt(formData.maxStudents),
            maxTeachers: parseInt(formData.maxTeachers),
            perTeacherCredits: parseInt(formData.perTeacherCredits) || 0,
            perStudentCredits: parseInt(formData.perStudentCredits) || 0,
            status: formData.status,
        };

        onSave(packageData);
    };

    const statusOptions = [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
    ];

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-3">
                    {editData ? t("Edit Package") : t("Create New Package")}
                    {editData && (
                        <AutoSaveIndicator
                            status={pkgAutoStatus}
                            lastSavedAt={pkgAutoAt}
                            isOnline={pkgAutoOnline}
                        />
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <InputComponent
                                label={t("Package Name")}
                                type="text"
                                name="name"
                                value={formData.name}
                                setValue={(value) => handleChange("name", value)}
                                placeholder={t("Enter package name")}
                                isInvalid={!!errors.name}
                                errorMessage={errors.name}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {t("Status")} <span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                    options={statusOptions}
                                    value={formData.status}
                                    onChange={(value) => {
                                        handleChange("status", value);
                                        // Dropdown = immediate save (edit mode).
                                        if (editData && editData._id) {
                                            savePkgImmediately({ status: value });
                                        }
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <InputComponent
                                label={t("Price")}
                                type="number"
                                name="price"
                                value={formData.price}
                                setValue={(value) => handleChange("price", value)}
                                placeholder={t("Enter price")}
                                isInvalid={!!errors.price}
                                errorMessage={errors.price}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <InputComponent
                                label={t("Duration (months)")}
                                type="number"
                                name="duration"
                                value={formData.duration}
                                setValue={(value) => handleChange("duration", value)}
                                placeholder={t("Enter duration in months")}
                                isInvalid={!!errors.duration}
                                errorMessage={errors.duration}
                                required
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <InputComponent
                                label={t("Max Students")}
                                type="number"
                                name="maxStudents"
                                value={formData.maxStudents}
                                setValue={(value) => handleChange("maxStudents", value)}
                                placeholder={t("Enter max students")}
                                isInvalid={!!errors.maxStudents}
                                errorMessage={errors.maxStudents}
                                required
                            />
                        </Col>
                        <Col md={6}>
                            <InputComponent
                                label={t("Max Teachers")}
                                type="number"
                                name="maxTeachers"
                                value={formData.maxTeachers}
                                setValue={(value) => handleChange("maxTeachers", value)}
                                placeholder={t("Enter max teachers")}
                                isInvalid={!!errors.maxTeachers}
                                errorMessage={errors.maxTeachers}
                                required
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <InputComponent
                                label={t("Max Classes")}
                                type="number"
                                name="maxClasses"
                                value={formData.maxClasses}
                                setValue={(value) => handleChange("maxClasses", value)}
                                placeholder={t("Enter max classes (optional)")}
                            />
                        </Col>
                        <Col md={6}>
                            <InputComponent
                                label={t("Per Teacher Credits")}
                                type="number"
                                name="perTeacherCredits"
                                value={formData.perTeacherCredits}
                                setValue={(value) => handleChange("perTeacherCredits", value)}
                                placeholder={t("Enter credits per teacher (optional)")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <InputComponent
                                label={t("Per Student Credits")}
                                type="number"
                                name="perStudentCredits"
                                value={formData.perStudentCredits}
                                setValue={(value) => handleChange("perStudentCredits", value)}
                                placeholder={t("Enter credits per student (optional)")}
                            />
                        </Col>
                    </Row>

                    <TextArea
                        label={t("Description")}
                        name="description"
                        value={formData.description}
                        setValue={(value) => handleChange("description", value)}
                        placeholder={t("Enter package description")}
                        rows={3}
                    />

                    <TextArea
                        label={t("Features (comma separated)")}
                        name="features"
                        value={formData.features}
                        setValue={(value) => handleChange("features", value)}
                        placeholder={t("Enter features separated by commas")}
                        rows={3}
                    />

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button variant="secondary" onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                        <Button variant="primary" type="submit" className="border-0 button-style">
                            {editData ? t("Update Package") : t("Create Package")}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreatePackageModal;
